import { Button, Input, Table } from '@/components/commons';
import { APP_ROUTER } from '@/routes/routes';
import { CloseOutlined, LeftOutlined, UploadOutlined } from '@ant-design/icons';
import { Badge, Form, Radio, Upload, Image, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { ImageService } from '@/services/image';
import { CompanyService } from '@/services/company';
import { ENDPOINT } from '@/services/endpoint';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import { useQuery } from 'react-query';
import { ICreateCompanyRequest } from '@/types/request/company';
import { IResponseError } from '@/types/common';
import { CsvUploadModal } from './CsvUploadModal';
import type { IInviteCodeRow, IValidatedErrorRow } from './types';
import { validateInviteCodeRows } from './validateInviteCodes';
import { toCompanyInviteCodesDto } from './inviteCodeDto';
import { ERROR, ERROR_MESSAGE, SUCCESS_MESSAGE } from '@/utils/constants/messages';

interface IProps {
  title?: string;
}

export const CompanyCreate: React.FC<IProps> = ({ title }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isShowService, setIsShowService] = useState<boolean>(false);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [inviteCodes, setInviteCodes] = useState<IInviteCodeRow[]>([]);
  const [errorRows, setErrorRows] = useState<IValidatedErrorRow[]>([]);
  const [successRows, setSuccessRows] = useState<IInviteCodeRow[]>([]);

  const handleCsvRegister = async (rows: IInviteCodeRow[]) => {
    const codes = rows
      .map((r) => String(r.code ?? '').trim())
      .filter((c) => c !== '');

    let conflictingCodes: string[] = [];
    try {
      const res = await CompanyService.checkInviteCode({ codes });
      conflictingCodes = res?.data?.conflictingCodes ?? [];
    } catch (e) {

    }

    const { errors, success } = validateInviteCodeRows(rows, conflictingCodes);
    // Re-upload should replace both success and error data
    setInviteCodes(success.map((r, i) => ({ ...r, id: i + 1 })));
    setErrorRows(errors);
    setSuccessRows(success);
  };

  const {
    data: articleSelectRes,
    isFetching: loadingArticles,
    refetch: refetchArticles,
  } = useQuery(
    [ENDPOINT.COMPANY.GET_ARTICLES],
    () => CompanyService.getArticles(),
  );

  const articleOptions =
    articleSelectRes?.data?.map((a) => ({
      value: a.id,
      label: a.name,
    })) ?? [];

  type ErrorRowWithKey = IValidatedErrorRow & { _key: number };
  const errorColumns: ColumnsType<ErrorRowWithKey> = [
    {
      title: 'ID',
      key: 'id',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.id.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.id.value,
    },
    {
      title: 'コード',
      key: 'code',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.code.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.code.value,
    },
    {
      title: '有効開始日',
      key: 'valid_from',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.valid_from.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.valid_from.value,
    },
    {
      title: '有効終了日',
      key: 'valid_to',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.valid_to.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.valid_to.value,
    },
    {
      title: 'CogEvo 有効フラグ',
      key: 'cog_evo',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.cog_evo.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.cog_evo.value,
    },
    {
      title: 'のうKNOW 有効フラグ',
      key: 'nounow',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.nounow.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.nounow.value,
    },
    {
      title: '有効上限数',
      key: 'limit',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.limit.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.limit.value,
    },
  ];

  return (
    <div className='p-post-list'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[24px] mx-auto w-full max-w-[1280px] pb-10'>

        <div className='text-[20px] font-bold'>団体情報の登録</div>

        <Form
          layout='vertical'
          className='flex flex-col gap-8'
          form={form}
          validateTrigger='onSubmit'
          onFinish={async (values) => {
            if (errorRows.length > 0) return;
            const {
              companyName,
              bannerImage,
              relatedArticleIds,
              serviceName,
              isShowService: showService,
              ...rest
            } = values as any;

            const body: ICreateCompanyRequest = {
              name: (companyName ?? '').trim(),
              isShowService: !!showService,
              status: 1,
            };

            const companyInviteCodes = toCompanyInviteCodesDto(successRows);
            if (companyInviteCodes.length > 0) {
              body.companyInviteCodes = companyInviteCodes;
            }

            if (showService) {
              let imageUrl: string | undefined;
              try {
                if (bannerImage instanceof File) {
                  const response =
                    await ImageService.uploadFile(bannerImage);
                  imageUrl = response?.data?.url ?? undefined;
                }
              } catch (error) {
                console.error('Upload banner image failed', error);
                notificationMessage({
                  message: 'バナー画像のアップロードに失敗しました。',
                  type: NOTIFY_STATUS.ERROR,
                });
                return;
              }
              body.serviceName = (serviceName ?? '').trim();
              body.imageUrl = imageUrl ?? '';
              body.articles = (relatedArticleIds || []).map((id: any) =>
                Number(id),
              );
            }

            setSubmitting(true);
            try {
              await CompanyService.create(body);
              notificationMessage({
                message: SUCCESS_MESSAGE.CREATE_COMPANY,
                type: NOTIFY_STATUS.SUCCESS,
              });
              navigate(APP_ROUTER.COMPANY.LIST);
            } catch (error) {
              const err = error as IResponseError;
              if (err?.message === 'EXIST_COMPANY_NAME') {
                form.setFields([
                  {
                    name: 'companyName',
                    errors: ['団体名は既に存在します。'],
                  },
                ]);
                return;
              }
              if (err?.message === ERROR.ARTICLE_ASSIGNED_TO_OTHER_COMPANY) {
                notificationMessage({
                  message: ERROR_MESSAGE.ARTICLE_ASSIGNED_TO_OTHER_COMPANY,
                  type: NOTIFY_STATUS.ERROR,
                });
                form.setFieldsValue({ relatedArticleIds: [] });
                await refetchArticles();
                return;
              }
              const message =
                typeof err?.message === 'string'
                  ? err.message
                  : '保存に失敗しました。';
              notificationMessage({
                message,
                type: NOTIFY_STATUS.ERROR,
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <section className='bg-[white] rounded-[8px] px-6 py-4'>
            <Form.Item
              label={
                <span className='flex items-center gap-2 font-bold'>
                  団体名
                  <Badge status='error' />
                </span>
              }
              name='companyName'
              validateTrigger={['onChange', 'onSubmit']}
              rules={[
                {
                  validator: (_, value) => {
                    const v =
                      typeof value === 'string' ? value.trim() : '';
                    if (!v) {
                      return Promise.reject(
                        new Error('団体名は必須です。'),
                      );
                    }
                    if (v.length > 50) {
                      return Promise.reject(
                        new Error(
                          '団体名は50文字以内で入力してください。',
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder='例：〇〇株式会社'
                maxLength={50}
                onBlur={(e) => {
                  const raw = e.target.value ?? '';
                  const trimmed = raw.trim();
                  if (trimmed !== raw) {
                    form.setFieldValue('companyName', trimmed);
                  }
                }}
              />
            </Form.Item>
          </section>

          {/* 解放コンテンツの設定 */}
          <section className='bg-[white] rounded-[8px]'>
            <div className=' font-semibold text-[16px] w-full border-b border-gray-200 px-6 py-4'>
              解放コンテンツの設定
            </div>
            <Form.Item
              label={
                <span className='flex items-center gap-2 font-bold'>
                  解放コンテンツの有無
                  <Badge status='error' />
                </span>
              }
              name='isShowService'
              initialValue={false}
              className='px-6 pt-4'
            >
              <Radio.Group
                value={isShowService}
                onChange={(e) => {
                  const value = e.target.value;
                  setIsShowService(value);
                  form.setFieldsValue({ isShowService: value });
                  if (!value) {
                    form.resetFields(['serviceName', 'bannerImage', 'relatedArticle']);
                    setBannerPreview(null);
                  }
                }}
              >
                <Radio value={true}>あり</Radio>
                <Radio value={false}>なし</Radio>
              </Radio.Group>
            </Form.Item>

            {isShowService && (
              <div className='mt-6 flex flex-col gap-4'>
                <Form.Item
                className='px-6'
                  label={
                    <span className='flex items-center gap-2 font-bold'>
                      サービス名
                      <Badge status='error' />
                    </span>
                  }
                  name='serviceName'
                  validateTrigger={['onChange', 'onSubmit']}
                  rules={[
                    {
                      validator: (_, value) => {
                        const v =
                          typeof value === 'string' ? value.trim() : '';
                        if (!v) {
                          return Promise.reject(
                            new Error('サービス名は必須です。'),
                          );
                        }
                        if (v.length > 30) {
                          return Promise.reject(
                            new Error(
                              'サービス名は30文字以内で入力してください。',
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder='例：〇〇株式会社'
                    maxLength={30}
                    onBlur={(e) => {
                      const raw = e.target.value ?? '';
                      const trimmed = raw.trim();
                      if (trimmed !== raw) {
                        form.setFieldValue('serviceName', trimmed);
                      }
                    }}
                  />
                </Form.Item>

                <Form.Item
                  className='px-6'
                  label={
                    <span className='flex items-center gap-2 font-bold'>
                      バナー画像
                      <Badge status='error' />
                    </span>
                  }
                  name='bannerImage'
                  rules={
                    isShowService
                      ? [
                        {
                          required: true,
                          message: 'バナー画像をアップロードしてください。',
                        },
                      ]
                      : []
                  }
                >
                  <>
                    <div className='flex items-baseline gap-2 pt-2'>
                      {!bannerPreview ? (
                        <Upload
                          maxCount={1}
                          listType='text'
                          showUploadList={false}
                          customRequest={() => { }}
                          beforeUpload={(file: File) => {
                            const fileExtension = file.name
                              .split('.')
                              .pop()
                              ?.toLowerCase();

                            if (file.size > FILE_SIZE_LIMIT) {
                              form.setFields([
                                {
                                  name: 'bannerImage',
                                  errors: ['画像のサイズは10MBを超えています。'],
                                },
                              ]);
                              return Upload.LIST_IGNORE;
                            }

                            if (
                              !SUPPORTED_EXTENSION.includes(
                                fileExtension || '',
                              ) ||
                              !SUPPORTED_FORMATS.includes(file.type)
                            ) {
                              form.setFields([
                                {
                                  name: 'bannerImage',
                                  errors: [
                                    '画像は .jpg、.png、.jpeg 形式ではありません。',
                                  ],
                                },
                              ]);
                              return Upload.LIST_IGNORE;
                            }

                            const reader = new FileReader();
                            reader.onload = () => {
                              setBannerPreview(reader.result as string);
                              form.setFieldsValue({ bannerImage: file });
                              form.setFields([
                                { name: 'bannerImage', errors: [] },
                              ]);
                            };
                            reader.readAsDataURL(file);

                            return Upload.LIST_IGNORE;
                          }}
                        >
                          <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center bg-white cursor-pointer'>
                            <Button
                              type='default'
                              className='text-[#84ab42] border border-[#84ab42] font-semibold'
                              icon={<UploadOutlined />}
                            >
                              画像を選択
                            </Button>
                          </div>
                        </Upload>
                      ) : (
                        <div className='relative inline-block'>
                          <Image
                            height={100}
                            src={bannerPreview || FALLBACK_URL}
                            className='block rounded-lg'
                            style={{ width: 'auto', maxWidth: 'none' }}
                            fallback={FALLBACK_URL}
                          />
                          <Button
                            type='primary'
                            shape='circle'
                            icon={<CloseOutlined />}
                            size='small'
                            onClick={() => {
                              setBannerPreview(null);
                              form.setFieldsValue({ bannerImage: undefined });
                            }}
                            className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
                          />
                        </div>
                      )}
                    </div>
                    <p className='title-calendar font-medium text-xs mt-2'>
                      <span className='font-bold mr-1'>※</span>
                      1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
                    </p>
                  </>
                </Form.Item>

                <Form.Item
                  className='px-6'
                  label={
                    <span className='flex items-center gap-2 font-bold'>
                      限定記事の選択
                    </span>
                  }
                  name='relatedArticleIds'
                  rules={
                    isShowService
                      ? [
                        {
                          required: false,
                        },
                      ]
                      : []
                  }
                >
                  <Select
                    mode='multiple'
                    placeholder='紐づける記事を検索...'
                    style={{ width: '100%' }}
                    options={articleOptions}
                    loading={loadingArticles}
                    filterOption={(input, option) =>
                      String(
                        (option as any)?.label ??
                        (option as any)?.children ??
                        '',
                      )
                        .toLowerCase()
                        .includes(input.toLowerCase()) ?? false
                    }
                  />
                </Form.Item>
              </div>
            )}
          </section>

          {/* 団体コードの追加 */}
          <section className='rounded-[8px] py-5 flex flex-col gap-4'>
            <div className='font-bold text-[20px]'>団体コードの追加</div>
            {successRows.length + errorRows.length > 0 && (
              <div className='text-[20px] font-bold'>
                結果：全{successRows.length + errorRows.length}件
                {successRows.length > 0 && ` 成功${successRows.length}件`}
                {errorRows.length > 0 && ` 失敗${errorRows.length}件`}
              </div>
            )}
            <div>
              <Button type='primary' onClick={() => setCsvModalOpen(true)}>
                + CSVのアップロード
              </Button>
            </div>

            <CsvUploadModal
              open={csvModalOpen}
              onClose={() => setCsvModalOpen(false)}
              onRegister={handleCsvRegister}
            />
            {
              !(!errorRows.length && successRows.length) && (
                <Table
                  className={
                    errorRows.length > 0
                      ? 'company-create-error-table company-create-error-table--with-title'
                      : 'company-create-error-table'
                  }
                  rowKey='_key'
                  bordered
                  columns={errorColumns}
                  dataSource={errorRows.map((row, i) => ({ ...row, _key: i }))}
                  {...(!!errorRows.length && {
                    title: () => (
                      <div className='font-bold'>
                        登録に失敗したコード
                      </div>
                    ),
                  })}
                  locale={{
                    emptyText: (
                      <div className='py-10 text-center text-[#666] text-sm leading-6'>
                        <div className='mb-4 font-semibold'>データがありません</div>
                        <div className='inline-block text-left'>
                          <div>以下の要件でCSVを作成してください。</div>
                          <div>・id：通し番号</div>
                          <div>・code：メンバーに配布するコード（共通：ユニークコード）</div>
                          <div>・valid_from：有効開始日（YYYY-MM-DD）</div>
                          <div>・valid_to：有効終了日（YYYY-MM-DD）</div>
                          <div>・cog_evo：1=ON / 0=OFF</div>
                          <div>・nounow：1=ON / 0=OFF</div>
                          <div>・limit：使用上限数</div>
                        </div>
                      </div>
                    ),
                  }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                    showTotal: (total, [start, end]) =>
                      `${start}~${end}件表示 / 全${total}行`,
                    position: ['bottomRight'],
                    className: 'error-table-pagination',
                  }}
                />
              )
            }
          </section>

          <div className='flex justify-center gap-4 pt-4'>
            <Button
              className='min-w-[120px]'
              onClick={() => navigate(APP_ROUTER.COMPANY.LIST)}
            >
              キャンセル
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              className='min-w-[120px]'
              loading={submitting}
              disabled={errorRows.length > 0}
            >
              保存
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
